<template>
  <div class="calendar-view">
    <input
      type="text"
      placeholder="Select a date range"
      readonly
      :class="iclass"
      :value="formattedRange"
      @focus="showCalendar = true"
    />
    <div v-if="showCalendar" class="calendar-popup">
      <div class="calendar-header">
        <span @click="toggleMonthYearPickerMode">
          <span v-if="isMonthYearPickerMode">{{ currentMonthName }} {{ currentYear }}</span>
          <span v-else>{{ months[currentMonth] }} {{ currentYear }}</span>
        </span>
        <span @click="prevMonth" v-if="!isMonthYearPickerMode">«</span>
        <span @click="nextMonth" v-if="!isMonthYearPickerMode">»</span>
      </div>

      <div v-if="isMonthYearPickerMode" class="month-year-picker">
        <select v-model="selectedMonth" @change="updateMonth">
          <option v-for="(month, index) in months" :key="index" :value="index">{{ month }}</option>
        </select>
        <select v-model="selectedYear" @change="updateYear">
          <option v-for="year in yearRange" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>

      <div class="calendar-days">
        <span v-for="day in daysOfWeek" :key="day">{{ day }}</span>
      </div>
      <div class="calendar-dates">
        <template v-for="(date, index) in daysInMonth" :key="index">
          <span
            v-if="date"
            :style="{ gridColumnStart: index === 0 ? firstDayOfMonth + 1 : 'auto' }"
            :class="[
              'calendar-date',
              { today: isToday(date) },
              { selected: isInRange(date) },
              { start: isStartDate(date) },
              { end: isEndDate(date) }
            ]"
            @click="selectDate(date)"
          >
            {{ date.getDate() }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  props: {
    modelValue: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
    iclass: String,
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const showCalendar = ref(false);
    const today = new Date();
    const currentYear = ref(today.getFullYear());
    const currentMonth = ref(today.getMonth());
    const selectedStartDate = ref(props.modelValue.start ? new Date(props.modelValue.start) : null);
    const selectedEndDate = ref(props.modelValue.end ? new Date(props.modelValue.end) : null);
    const selectingStart = ref(true);

    const isMonthYearPickerMode = ref(false);
    const selectedMonth = ref(currentMonth.value);
    const selectedYear = ref(currentYear.value);

    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const currentMonthName = computed(() => months[currentMonth.value]);

    const formattedRange = computed(() => {
      const format = (date) => date?.toLocaleDateString("en-CA") || "";
      return selectedStartDate.value && selectedEndDate.value
        ? `${format(selectedStartDate.value)} - ${format(selectedEndDate.value)}`
        : "";
    });

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const yearRange = computed(() => {
      return Array.from({ length: 121 }, (_, i) => 1900 + i);
    });

    const firstDayOfMonth = computed(() => {
      return new Date(currentYear.value, currentMonth.value, 1).getDay();
    });

    const daysInMonth = computed(() => {
      const days = [];
      const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0).getDate();
      for (let i = 1; i <= lastDay; i++) {
        days.push(new Date(currentYear.value, currentMonth.value, i));
      }
      return days;
    });

    const isToday = (date) => {
      const now = new Date();
      return (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      );
    };

    const isStartDate = (date) => selectedStartDate.value && date.toDateString() === selectedStartDate.value.toDateString();
    const isEndDate = (date) => selectedEndDate.value && date.toDateString() === selectedEndDate.value.toDateString();

    const isInRange = (date) => {
      if (!selectedStartDate.value || !selectedEndDate.value) return false;
      return date >= selectedStartDate.value && date <= selectedEndDate.value;
    };

    const selectDate = (date) => {
        if (selectingStart.value) {
            selectedStartDate.value = date;
            selectedEndDate.value = null;
            selectingStart.value = false;
        } else {
            if (date < selectedStartDate.value) {
            selectedEndDate.value = selectedStartDate.value;
            selectedStartDate.value = date;
            } else {
            selectedEndDate.value = date;
            }
            selectingStart.value = true;
            showCalendar.value = false;

            const formatLocalDate = (d) =>
            d.getFullYear() +
            '-' + String(d.getMonth() + 1).padStart(2, '0') +
            '-' + String(d.getDate()).padStart(2, '0');

            emit("update:modelValue", {
            start: formatLocalDate(selectedStartDate.value),
            end: formatLocalDate(selectedEndDate.value),
            });
        }
    };

    const prevMonth = () => {
      if (currentMonth.value === 0) {
        currentMonth.value = 11;
        currentYear.value--;
      } else {
        currentMonth.value--;
      }
    };

    const nextMonth = () => {
      if (currentMonth.value === 11) {
        currentMonth.value = 0;
        currentYear.value++;
      } else {
        currentMonth.value++;
      }
    };

    const toggleMonthYearPickerMode = () => {
      isMonthYearPickerMode.value = !isMonthYearPickerMode.value;
    };

    const updateMonth = () => {
      currentMonth.value = selectedMonth.value;
      isMonthYearPickerMode.value = false;
    };

    const updateYear = () => {
      currentYear.value = selectedYear.value;
      isMonthYearPickerMode.value = false;
    };

    const setDefaultRange = (startDate, endDate) => {
      selectedStartDate.value = new Date(startDate);
      selectedEndDate.value = new Date(endDate);
      selectingStart.value = true; // Reset for next user selection
    };

    return {
      showCalendar,setDefaultRange,
      currentYear,
      currentMonth,
      currentMonthName,
      selectedStartDate,
      selectedEndDate,
      selectingStart,
      isMonthYearPickerMode,
      selectedMonth,
      selectedYear,
      months,
      daysOfWeek,
      yearRange,
      formattedRange,
      firstDayOfMonth,
      daysInMonth,
      isToday,
      isStartDate,
      isEndDate,
      isInRange,
      selectDate,
      prevMonth,
      nextMonth,
      toggleMonthYearPickerMode,
      updateMonth,
      updateYear
    };
  }
};
</script>
  
  <style scoped>
  .calendar-view { position: relative; }
  .calendar-view input { margin-bottom: 0 !important; }
  
  .calendar-popup {
    position: absolute;
    top: 100%;
    left: 0;
    background: var(--white-color);
    border: 1px solid var(--data-table-border);
    border-radius: 5px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
    width: 250px;
    padding: 10px;
    color: var(--black);
  }
  
  .calendar-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .calendar-header > span { cursor: pointer; }
  
  .calendar-days, .calendar-dates {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    gap: 5px;
  }
  
  .calendar-date {
    padding: 5px;
    cursor: pointer;
    border-radius: 4px;
  }
  
  .calendar-date.today {
    font-weight: bold;
    color: var(--primary-color);
  }
  
  .calendar-date.selected {
    background-color: var(--primary-color);
    color: white;
  }
  
  .calendar-date.start {
    background-color: var(--primary-color);
    color: white;
    border-radius: 50% 0 0 50%;
  }
  
  .calendar-date.end {
    background-color: var(--primary-color);
    color: white;
    border-radius: 0 50% 50% 0;
  }
  </style>
  